import React, { useState, useEffect } from 'react';
import Navbar from '../Components/Navbar';
import { Footer } from '../Components/Footer';
import { 
  Monitor, 
  MessageCircle, 
  FileText,
  X,
  Linkedin,
  Shield,
  Lock,
  Users,
  Check,
  ChevronRight,
  ArrowRight,
  AlertCircle,
  Fingerprint,
  Database,
  Smartphone,
  FileSearch,
  Palette,
  Settings
} from 'lucide-react';
import { useTranslation } from '../../TranslationContext';
import SubHeader from '../Components/SubHeader';

interface TeamMember {
  name: string;
  title: string;
  image: string;
  bio: string;
  linkedin?: string;
}

interface Quote {
  text: string;
  author: string;
  title: string;
}

interface CustomModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

interface CoreValue {
  icon: React.ElementType;
  title: string;
  description: string;
}

interface Service {
  icon: React.ElementType;
  title: string;
  description: string;
}

const CustomModal: React.FC<CustomModalProps> = ({ isOpen, onClose, children }) => {
  const { t } = useTranslation();
  const [isAnimating, setIsAnimating] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div className={`fixed inset-0 z-50 overflow-auto bg-slate-900 bg-opacity-40 backdrop-blur-sm flex justify-center items-center transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
      <div className={`relative bg-white rounded-xl p-8 max-w-2xl m-4 shadow-xl transition-all duration-300 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'} border border-slate-200`}>
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors duration-200"
          aria-label={t('close')}
        >
          <X size={24} />
        </button>
        {children}
      </div>
    </div>
  );
};

export const About: React.FC = () => {
  const { t } = useTranslation();
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      
      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        
        if (sectionTop < window.innerHeight / 2) {
          setActiveSection(section.id);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const teamMembers: TeamMember[] = [
    {
      name: "xxx xxx",
      title: t('ceo'),
      image: "/avatar.png",
      bio: t('ceo_bio'),
      linkedin: "https://linkedin.com"
    },
    {
      name: "YYY YYY",
      title: t('cto'),
      image: "/avatar.png",
      bio: t('cto_bio'),
      linkedin: "https://linkedin.com"
    },
    {
      name: "ZZZ ZZZ",
      title: t('cmo'),
      image: "/avatar.png",
      bio: t('cmo_bio'),
      linkedin: "https://linkedin.com"
    }
  ];

  const quote: Quote = {
    text: t('leadership_quote'),
    author: "XXX XXX",
    title: t('ceo')
  };

  const connectOptions = [
    {
      icon: <Monitor className="w-12 h-12 text-slate-400 group-hover:text-white transition-colors duration-300" />,
      title: t('free_demo'),
      description: t('free_demo_description'),
      link: "#"
    },
    {
      icon: <MessageCircle className="w-12 h-12 text-slate-400 group-hover:text-white transition-colors duration-300" />,
      title: t('contact_us'),
      description: t('contact_us_description'),
      link: "#"
    },
    {
      icon: <FileText className="w-12 h-12 text-slate-400 group-hover:text-white transition-colors duration-300" />,
      title: t('blog'),
      description: t('blog_description'),
      link: "#"
    }
  ];

  const coreValues: CoreValue[] = [
    {
      icon: Shield,
      title: t('core_value_security_title'),
      description: t('core_value_security_description')
    },
    {
      icon: Users,
      title: t('core_value_trust_title'),
      description: t('core_value_trust_description')
    },
    {
      icon: Lock,
      title: t('core_value_innovation_title'),
      description: t('core_value_innovation_description')
    }
  ];

  const ourValues = [
    {
      icon: Users,
      title: t('value_customer_service_title'),
      description: t('value_customer_service_description')
    },
    {
      icon: Lock,
      title: t('value_confidentiality_title'),
      description: t('value_confidentiality_description')
    },
    {
      icon: AlertCircle,
      title: t('value_transparency_title'),
      description: t('value_transparency_description')
    },
    {
      icon: Fingerprint,
      title: t('value_trust_title'),
      description: t('value_trust_description')
    },
    {
      icon: Shield,
      title: t('value_responsibility_title'),
      description: t('value_responsibility_description')
    }
  ];

  const ourServices: Service[] = [
    {
      icon: Shield,
      title: t('service_cybersecurity_title'),
      description: t('service_cybersecurity_description')
    },
    {
      icon: Monitor,
      title: t('service_it_architecture_title'),
      description: t('service_it_architecture_description')
    },
    {
      icon: Database,
      title: t('service_data_security_title'),
      description: t('service_data_security_description')
    },
    {
      icon: Smartphone,
      title: t('service_digitalization_title'),
      description: t('service_digitalization_description')
    },
    {
      icon: FileSearch,
      title: t('service_audit_title'),
      description: t('service_audit_description')
    },
    {
      icon: Palette,
      title: t('service_design_title'),
      description: t('service_design_description')
    },
    {
      icon: Settings,
      title: t('service_system_improvement_title'),
      description: t('service_system_improvement_description')
    }
  ];

  const handleNavigateTo = (path: string) => {
    window.location.href = path;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SubHeader isDarkMode={false} />

      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#2C3E50] to-[#4A5568] opacity-90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="prose max-w-none flex-1 text-center md:text-left">
              <h1 className="text-5xl font-bold text-white mb-6">
                {t('about_otenti')}<span className="text-[#F3DAB3]">.</span>
              </h1>
              <p className="text-slate-300 text-xl font-light leading-relaxed mb-8">
                {t('otenti_description')}
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <button 
                  className="px-6 py-3 bg-[#F3DAB3] text-[#2C3E50] rounded-lg hover:bg-[#DBC090] transition-colors duration-300 shadow-lg shadow-[#F3DAB3]/20"
                  onClick={() => handleNavigateTo('/services')}
                  aria-label={t('our_services')}
                >
                  {t('our_services')}
                </button>
                <button 
                  className="px-6 py-3 bg-transparent text-white border border-slate-400 rounded-lg hover:bg-slate-700 transition-colors duration-300"
                  onClick={() => handleNavigateTo('/contact')}
                  aria-label={t('contact_us')}
                >
                  {t('contact_us')}
                </button>
              </div>
            </div>

            <div className="flex-1 relative">
              <div className="absolute inset-0 bg-[#F3DAB3] rounded-2xl -rotate-6 opacity-20"></div>
              <img
                src="/home.jpeg"
                alt={t('about_otenti')}
                className="relative w-full h-auto rounded-2xl shadow-2xl border border-slate-700"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Notre Mission Section */}
      <section id="notre-mission" className={`py-20 bg-white transition-opacity duration-1000 ${activeSection === 'notre-mission' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
              {t('our_mission')}
            </h2>
            <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
            <p className="text-slate-600 max-w-3xl mx-auto text-lg">
              {t('mission_description_new')}
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 bg-[#F3DAB3] opacity-5 rounded-3xl"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10 p-8">
              <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
                <h3 className="text-2xl font-bold text-[#2C3E50] mb-4 flex items-center">
                  <Shield className="h-6 w-6 mr-2 text-[#F3DAB3]" />
                  {t('mission_commitment_title')}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t('mission_commitment_description')}
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
                <h3 className="text-2xl font-bold text-[#2C3E50] mb-4 flex items-center">
                  <Users className="h-6 w-6 mr-2 text-[#F3DAB3]" />
                  {t('mission_approach_title')}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t('mission_approach_description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Valeurs Section */}
      <section id="nos-valeurs" className={`py-20 bg-[#DDE6F1] transition-opacity duration-1000 ${activeSection === 'nos-valeurs' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
              {t('our_values')}
            </h2>
            <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
            <p className="text-slate-600 max-w-3xl mx-auto text-lg">
              {t('values_description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {ourValues.map((value, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl shadow-md transition-all duration-300 hover:shadow-xl border border-gray-200 group hover:-translate-y-2"
              >
                <div className="w-14 h-14 bg-[#F3DAB3] rounded-full mb-5 flex items-center justify-center group-hover:bg-[#2C3E50] transition-colors duration-300">
                  <value.icon className="h-7 w-7 text-[#2C3E50] group-hover:text-[#F3DAB3]" />
                </div>
                <h3 className="text-xl font-bold text-[#2C3E50] mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos Services Section */}
      <section id="nos-services" className={`py-20 bg-white transition-opacity duration-1000 ${activeSection === 'nos-services' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
              {t('our_services')}
            </h2>
            <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
            <p className="text-slate-600 max-w-3xl mx-auto text-lg">
              {t('services_description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ourServices.map((service, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-xl shadow-md transition-all duration-300 hover:shadow-xl border border-gray-200 group hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-[#F3DAB3] bg-opacity-20 rounded-lg mb-6 flex items-center justify-center group-hover:bg-[#2C3E50] group-hover:bg-opacity-10 transition-colors duration-300">
                  <service.icon className="h-8 w-8 text-[#2C3E50] group-hover:text-[#F3DAB3]" />
                </div>
                <h3 className="text-xl font-bold text-[#2C3E50] mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
                <div className="mt-6">
                  <button 
                    className="inline-flex items-center text-[#F3DAB3] hover:text-[#DBC090] transition-colors duration-200"
                    onClick={() => handleNavigateTo('/services')}
                    aria-label={t('learn_more')}
                  >
                    <span>{t('learn_more')}</span>
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <button 
              className="px-8 py-4 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1A2533] transition-colors duration-300 shadow-lg inline-flex items-center"
              onClick={() => handleNavigateTo('/services')}
              aria-label={t('discover_all_services')}
            >
              <span>{t('discover_all_services')}</span>
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Our Mission Section (Original) */}
      <section id="mission" className={`py-20 bg-gradient-to-b from-white to-[#DDE6F1] transition-opacity duration-1000 ${activeSection === 'mission' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
              {t('our_mission')}
            </h2>
            <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              {t('mission_description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-xl shadow-md transition-all duration-300 hover:shadow-xl border border-gray-200 group hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-[#F3DAB3] rounded-full mb-6 flex items-center justify-center group-hover:bg-[#2C3E50] transition-colors duration-300">
                  <value.icon className="h-8 w-8 text-[#2C3E50] group-hover:text-[#F3DAB3]" />
                </div>
                <h3 className="text-xl font-bold text-[#2C3E50] mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <button 
              className="px-6 py-3 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1A2533] transition-colors duration-300 shadow-md flex items-center mx-auto"
              onClick={() => handleNavigateTo('/values')}
              aria-label={t('learn_about_our_values')}
            >
              {t('learn_about_our_values')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Leadership Quote */}
      <section id="quote" className={`py-24 bg-[#2C3E50] transition-opacity duration-1000 ${activeSection === 'quote' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white bg-opacity-5 rounded-xl p-12 shadow-2xl relative overflow-hidden backdrop-blur-sm">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('/pattern.svg')] opacity-5"></div>
            <div className="relative z-10">
              <div className="flex justify-center mb-8">
                <div className="w-20 h-20 rounded-full bg-[#F3DAB3] bg-opacity-20 flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-[#F3DAB3]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
              </div>

              <p className="text-2xl md:text-3xl italic mb-8 font-light text-white text-center leading-relaxed">
                "{quote.text}"
              </p>

              <div className="flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-[#F3DAB3] bg-opacity-20 mr-4 flex items-center justify-center">
                  <span className="text-xl font-bold text-[#F3DAB3]">{quote.author.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-bold text-lg text-white">{quote.author}</p>
                  <p className="text-sm text-slate-400">{quote.title}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company History Timeline */}
      <section id="history" className={`py-24 bg-[#DDE6F1] transition-opacity duration-1000 ${activeSection === 'history' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
              {t('our_history')}
            </h2>
            <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              {t('history_description')}
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#F3DAB3] rounded-full hidden md:block"></div>
            
            <div className="space-y-16">
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-12 md:text-right">
                  <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                    <div className="text-[#2C3E50] font-bold text-xl mb-2">{t('history_2020_title')}</div>
                    <p className="text-slate-600">{t('history_2020_description')}</p>
                  </div>
                </div>
                <div className="md:w-1/2 flex justify-start md:justify-center py-4 md:px-0">
                  <div className="bg-[#F3DAB3] w-10 h-10 rounded-full flex items-center justify-center border-4 border-white shadow-md">
                    <span className="text-[#2C3E50] font-bold">20</span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row-reverse items-center">
                <div className="md:w-1/2 md:pl-12">
                  <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                    <div className="text-[#2C3E50] font-bold text-xl mb-2">{t('history_2022_title')}</div>
                    <p className="text-slate-600">{t('history_2022_description')}</p>
                  </div>
                </div>
                <div className="md:w-1/2 flex justify-end md:justify-center py-4 md:px-0">
                  <div className="bg-[#F3DAB3] w-10 h-10 rounded-full flex items-center justify-center border-4 border-white shadow-md">
                    <span className="text-[#2C3E50] font-bold">22</span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-12 md:text-right">
                  <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                    <div className="text-[#2C3E50] font-bold text-xl mb-2">{t('history_2024_title')}</div>
                    <p className="text-slate-600">{t('history_2024_description')}</p>
                  </div>
                </div>
                <div className="md:w-1/2 flex justify-start md:justify-center py-4 md:px-0">
                  <div className="bg-[#F3DAB3] w-10 h-10 rounded-full flex items-center justify-center border-4 border-white shadow-md">
                    <span className="text-[#2C3E50] font-bold">24</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className={`py-24 bg-white transition-opacity duration-1000 ${activeSection === 'team' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
              {t('our_leadership_team')}
            </h2>
            <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              {t('leadership_team_description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div 
                key={index} 
                className="group"
              >
                <div 
                  className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border border-gray-200"
                  onClick={() => setSelectedMember(member)}
                  role="button"
                  aria-label={t('view_bio')}
                  // aria-label={t('view_bio', { name: member.name })}
                >

                  <div className="relative w-40 h-40 mx-auto mb-6">
                    <div className="absolute inset-0 bg-[#F3DAB3] rounded-full opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full rounded-full object-cover relative z-10 border-4 border-white"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-[#2C3E50] mb-2">
                    {member.name}
                  </h3>
                  <p className="text-slate-400 mb-4">
                    {member.title}
                  </p>
                  <button className="inline-flex items-center text-[#F3DAB3] hover:text-[#DBC090] transition-colors duration-200">
                    <span>{t('read_bio')}</span>
                    <svg 
                      className="w-4 h-4 ml-1 group-hover:ml-2 transition-all duration-200" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Connect Section */}
      <section id="connect" className={`py-24 bg-gradient-to-r from-[#2C3E50] to-[#4A5568] transition-opacity duration-1000 ${activeSection === 'connect' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              {t('connect_with_otenti')}
            </h2>
            <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
            <p className="text-slate-300 max-w-2xl mx-auto text-lg">
              {t('connect_description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {connectOptions.map((option, index) => (
              <a
                key={index}
                href={option.link}
                className="group bg-[#2C3E50] hover:bg-[#F3DAB3] rounded-xl p-8 text-center transition-all duration-300 hover:-translate-y-2 border border-slate-600 hover:border-[#F3DAB3] shadow-lg"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-600 group-hover:bg-[#2C3E50] flex items-center justify-center transition-colors duration-300">
                  {option.icon}
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-[#2C3E50] mb-3 transition-colors duration-300">
                  {option.title}
                </h3>
                <p className="text-slate-400 group-hover:text-[#2C3E50] transition-colors duration-300">
                  {option.description}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className={`py-24 bg-[#DDE6F1] transition-opacity duration-1000 ${activeSection === 'expertise' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 order-2 md:order-1">
              <h2 className="text-3xl font-bold text-[#2C3E50] mb-6">{t('expertise_title')}</h2>
              <p className="text-[#2C3E50] leading-relaxed">
                {t('expertise_description')}
              </p>
              <ul className="space-y-4 mt-6">
                {[
                  t('expertise_item_1'),
                  t('expertise_item_2'),
                  t('expertise_item_3')
                ].map((text, index) => (
                  <li key={index} className="flex items-start">
                    <div className="bg-[#F3DAB3] p-1 rounded-full mr-3 shadow-sm">
                      <Check className="h-5 w-5 text-[#2C3E50]" />
                    </div>
                    <span className="text-[#2C3E50]">{text}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 pt-4">
                <button 
                  onClick={() => handleNavigateTo('/services')}
                  className="px-6 py-3 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1A2533] transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center"
                  aria-label={t('view_our_services')}
                >
                  {t('view_our_services')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                <button 
                  onClick={() => handleNavigateTo('/contact')}
                  className="px-6 py-3 border-2 border-[#2C3E50] text-[#2C3E50] rounded-lg hover:bg-[#2C3E50] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center"
                  aria-label={t('contact_our_team')}
                >
                  {t('contact_our_team')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="bg-white p-8 rounded-xl shadow-lg transform transition-all duration-500 hover:scale-105">
                <div className="h-48 bg-gradient-to-r from-[#2C3E50] to-[#4A5568] rounded-lg mb-6 flex items-center justify-center">
                  <Shield className="h-16 w-16 text-[#F3DAB3]" />
                </div>
                <h4 className="text-xl font-semibold text-[#2C3E50] mb-4">{t('security_excellence_title')}</h4>
                <p className="text-gray-600">
                  {t('security_excellence_description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="contact-cta" className={`py-20 bg-white transition-opacity duration-1000 ${activeSection === 'contact-cta' ? 'opacity-100' : 'opacity-80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#2C3E50] to-[#4A5568] rounded-2xl p-12 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('/pattern.svg')] opacity-10"></div>
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">{t('secure_digital_title')}</h2>
                <p className="text-slate-300 mb-8 leading-relaxed">
                  {t('secure_digital_description')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <button 
                    className="px-6 py-3 bg-[#F3DAB3] text-[#2C3E50] rounded-lg hover:bg-[#DBC090] transition-colors duration-300 shadow-lg flex items-center"
                    onClick={() => handleNavigateTo('/contact')}
                    aria-label={t('contact_us')}
                  >
                    <span>{t('contact_us')}</span>
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                  <button 
                    className="px-6 py-3 bg-transparent text-white border border-white rounded-lg hover:bg-white hover:text-[#2C3E50] transition-colors duration-300 flex items-center"
                    onClick={() => handleNavigateTo('/demo')}
                    aria-label={t('request_demo')}
                  >
                    <span>{t('request_demo')}</span>
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#F3DAB3] rounded-2xl -rotate-6 opacity-20"></div>
                  <img
                    src="/security.jpg"
                    alt={t('cybersecurity')}
                    className="relative w-full h-auto rounded-2xl shadow-2xl border border-slate-700"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Modal */}
      <CustomModal
        isOpen={selectedMember !== null}
        onClose={() => setSelectedMember(null)}
      >
        {selectedMember && (
          <div>
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
              <div className="relative w-32 h-32">
                <div className="absolute inset-0 bg-[#F3DAB3] rounded-full opacity-20"></div>
                <img
                  src={selectedMember.image}
                  alt={selectedMember.name}
                  className="w-full h-full rounded-full object-cover relative z-10 border-4 border-white"
                />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-bold text-[#2C3E50] mb-2">{selectedMember.name}</h3>
                <p className="text-slate-400 mb-4">{selectedMember.title}</p>
                {selectedMember.linkedin && (
                  <a 
                    href={selectedMember.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#F3DAB3] hover:text-[#DBC090] transition-colors duration-200"
                    aria-label={t('connect_on_linkedin')}
                  >
                    <Linkedin size={16} className="mr-2" />
                    <span>{t('connect_on_linkedin')}</span>
                  </a>
                )}
              </div>
            </div>
            <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
              <h4 className="text-lg font-medium text-[#2C3E50] mb-3">{t('biography')}</h4>
              <p className="text-slate-600 leading-relaxed">{selectedMember.bio}</p>
            </div>
          </div>
        )}
      </CustomModal>
      
      <Footer />
    </div>
  );
};

// import React, { useState, useEffect } from 'react';
// import Navbar from '../Components/Navbar';
// import { Footer } from '../Components/Footer';
// import { 
//   Monitor, 
//   MessageCircle, 
//   FileText,
//   X,
//   Linkedin,
//   Shield,
//   Lock,
//   Users,
//   Check,
//   ChevronRight,
//   ArrowRight,
//   AlertCircle,
//   Fingerprint,
//   Database,
//   Smartphone,
//   FileSearch,
//   Palette,
//   Settings
// } from 'lucide-react';
// import { useTranslation } from '../../TranslationContext';
// import SubHeader from '../Components/SubHeader';

// interface TeamMember {
//   name: string;
//   title: string;
//   image: string;
//   bio: string;
//   linkedin?: string;
// }

// interface Quote {
//   text: string;
//   author: string;
//   title: string;
// }

// interface CustomModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   children: React.ReactNode;
// }

// interface CoreValue {
//   icon: React.ElementType;
//   title: string;
//   description: string;
// }

// interface Service {
//   icon: React.ElementType;
//   title: string;
//   description: string;
// }

// const CustomModal: React.FC<CustomModalProps> = ({ isOpen, onClose, children }) => {
//   const [isAnimating, setIsAnimating] = useState(false);
  
//   useEffect(() => {
//     if (isOpen) {
//       setIsAnimating(true);
//     }
//   }, [isOpen]);

//   const handleClose = () => {
//     setIsAnimating(false);
//     setTimeout(() => {
//       onClose();
//     }, 300);
//   };

//   if (!isOpen && !isAnimating) return null;

//   return (
//     <div className={`fixed inset-0 z-50 overflow-auto bg-slate-900 bg-opacity-40 backdrop-blur-sm flex justify-center items-center transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
//       <div className={`relative bg-white rounded-xl p-8 max-w-2xl m-4 shadow-xl transition-all duration-300 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'} border border-slate-200`}>
//         <button 
//           onClick={handleClose}
//           className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors duration-200"
//         >
//           <X size={24} />
//         </button>
//         {children}
//       </div>
//     </div>
//   );
// };

// export const About: React.FC = () => {
//   const { t } = useTranslation();
//   const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
//   const [activeSection, setActiveSection] = useState<string>('');

//   useEffect(() => {
//     const handleScroll = () => {
//       const sections = document.querySelectorAll('section[id]');
      
//       sections.forEach(section => {
//         const sectionTop = section.getBoundingClientRect().top;
        
//         if (sectionTop < window.innerHeight / 2) {
//           setActiveSection(section.id);
//         }
//       });
//     };

//     window.addEventListener('scroll', handleScroll);
//     return () => window.removeEventListener('scroll', handleScroll);
//   }, []);

//   const teamMembers: TeamMember[] = [
//     {
//       name: "xxx xxx",
//       title: t('ceo'),
//       image: "/avatar.png",
//       bio: t('ceo_bio'),
//       linkedin: "https://linkedin.com"
//     },
//     {
//       name: "YYY YYY",
//       title: t('cto'),
//       image: "/avatar.png",
//       bio: t('cto_bio'),
//       linkedin: "https://linkedin.com"
//     },
//     {
//       name: "ZZZ ZZZ",
//       title: t('cmo'),
//       image: "/avatar.png",
//       bio: t('cmo_bio'),
//       linkedin: "https://linkedin.com"
//     }
//   ];

//   const quote: Quote = {
//     text: t('leadership_quote'),
//     author: "XXX XXX",
//     title: t('ceo')
//   };

//   const connectOptions = [
//     {
//       icon: <Monitor className="w-12 h-12 text-slate-400 group-hover:text-white transition-colors duration-300" />,
//       title: t('free_demo'),
//       description: t('free_demo_description'),
//       link: "#"
//     },
//     {
//       icon: <MessageCircle className="w-12 h-12 text-slate-400 group-hover:text-white transition-colors duration-300" />,
//       title: t('contact_us'),
//       description: t('contact_us_description'),
//       link: "#"
//     },
//     {
//       icon: <FileText className="w-12 h-12 text-slate-400 group-hover:text-white transition-colors duration-300" />,
//       title: t('blog'),
//       description: t('blog_description'),
//       link: "#"
//     }
//   ];

//   const coreValues: CoreValue[] = [
//     {
//       icon: Shield,
//       title: t('core_value_security_title'),
//       description: t('core_value_security_description')
//     },
//     {
//       icon: Users,
//       title: t('core_value_trust_title'),
//       description: t('core_value_trust_description')
//     },
//     {
//       icon: Lock,
//       title: t('core_value_innovation_title'),
//       description: t('core_value_innovation_description')
//     }
//   ];

//   // Nouvelles valeurs ajoutées selon les spécifications du client
//   const ourValues = [
//     {
//       icon: Users,
//       title: "Service à l'écoute",
//       description: "Un service centré sur le client, attentif à vos besoins spécifiques."
//     },
//     {
//       icon: Lock,
//       title: "Confidentialité",
//       description: "Protection rigoureuse de vos données et informations sensibles."
//     },
//     {
//       icon: AlertCircle,
//       title: "Transparence",
//       description: "Communication claire et honnête tout au long de notre collaboration."
//     },
//     {
//       icon: Fingerprint,
//       title: "Confiance",
//       description: "Des relations basées sur la fiabilité et l'intégrité professionnelle."
//     },
//     {
//       icon: Shield,
//       title: "Responsabilité",
//       description: "Un engagement total envers la qualité et la sécurité de nos solutions."
//     }
//   ];

//   // Services Otenti ajoutés selon les spécifications
//   const ourServices: Service[] = [
//     {
//       icon: Shield,
//       title: "CyberSécurité",
//       description: "Protection avancée contre les menaces numériques et sécurisation de vos systèmes."
//     },
//     {
//       icon: Monitor,
//       title: "Architecture IT",
//       description: "Conception et implémentation d'infrastructures informatiques robustes et évolutives."
//     },
//     {
//       icon: Database,
//       title: "Data Security",
//       description: "Solutions spécialisées pour la protection et la gestion sécurisée de vos données."
//     },
//     {
//       icon: Smartphone,
//       title: "Digitalisation",
//       description: "Transformation numérique adaptée à vos besoins spécifiques et à votre secteur d'activité."
//     },
//     {
//       icon: FileSearch,
//       title: "Audit",
//       description: "Évaluation approfondie de vos systèmes pour identifier les vulnérabilités et opportunités d'amélioration."
//     },
//     {
//       icon: Palette,
//       title: "Design",
//       description: "Conception d'interfaces intuitives et ergonomiques pour une expérience utilisateur optimale."
//     },
//     {
//       icon: Settings,
//       title: "Amélioration des Systèmes",
//       description: "Optimisation de vos infrastructures existantes pour une performance et une sécurité accrues."
//     }
//   ];

//   const handleNavigateTo = (path: string) => {
//     window.location.href = path;
//   };

//   return (
//     <div className="min-h-screen bg-white">
//       <Navbar />
//       <SubHeader isDarkMode={false} />

//       {/* Hero Section */}
//       <div className="relative">
//         <div className="absolute inset-0 bg-gradient-to-r from-[#2C3E50] to-[#4A5568] opacity-90"></div>
//         <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
//           <div className="flex flex-col md:flex-row gap-12 items-center">
//             {/* Text content */}
//             <div className="prose max-w-none flex-1 text-center md:text-left">
//               <h1 className="text-5xl font-bold text-white mb-6">
//                 {t('about_otenti')}<span className="text-[#F3DAB3]">.</span>
//               </h1>
//               <p className="text-slate-300 text-xl font-light leading-relaxed mb-8">
//                 {t('otenti_description')}
//               </p>
//               <div className="flex flex-wrap gap-4 justify-center md:justify-start">
//                 <button 
//                   className="px-6 py-3 bg-[#F3DAB3] text-[#2C3E50] rounded-lg hover:bg-[#DBC090] transition-colors duration-300 shadow-lg shadow-[#F3DAB3]/20"
//                   onClick={() => handleNavigateTo('/services')}
//                 >
//                   {t('our_services')}
//                 </button>
//                 <button 
//                   className="px-6 py-3 bg-transparent text-white border border-slate-400 rounded-lg hover:bg-slate-700 transition-colors duration-300"
//                   onClick={() => handleNavigateTo('/contact')}
//                 >
//                   {t('contact_us')}
//                 </button>
//               </div>
//             </div>

//             {/* Image */}
//             <div className="flex-1 relative">
//               <div className="absolute inset-0 bg-[#F3DAB3] rounded-2xl -rotate-6 opacity-20"></div>
//               <img
//                 src="/home.jpeg"
//                 alt={t('about_otenti')}
//                 className="relative w-full h-auto rounded-2xl shadow-2xl border border-slate-700"
//               />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Notre Mission Section - NOUVELLE SECTION AJOUTÉE */}
//       <section id="notre-mission" className={`py-20 bg-white transition-opacity duration-1000 ${activeSection === 'notre-mission' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
//               Notre Mission
//             </h2>
//             <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
//             <p className="text-slate-600 max-w-3xl mx-auto text-lg">
//               Vous offrir des solutions à la hauteur de vos besoins, innovantes, fiables, sophistiquées et de grande qualité.
//             </p>
//           </div>
          
//           <div className="relative">
//             <div className="absolute inset-0 bg-[#F3DAB3] opacity-5 rounded-3xl"></div>
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10 p-8">
//               <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
//                 <h3 className="text-2xl font-bold text-[#2C3E50] mb-4 flex items-center">
//                   <Shield className="h-6 w-6 mr-2 text-[#F3DAB3]" />
//                   Notre Engagement
//                 </h3>
//                 <p className="text-slate-600 leading-relaxed">
//                   En tant que l'un des produits de Tsuriel Cyber et IT, Otenti s'engage à fournir des services de cybersécurité de premier ordre, adaptés aux défis actuels et futurs du monde numérique.
//                 </p>
//               </div>
//               <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
//                 <h3 className="text-2xl font-bold text-[#2C3E50] mb-4 flex items-center">
//                   <Users className="h-6 w-6 mr-2 text-[#F3DAB3]" />
//                   Notre Approche
//                 </h3>
//                 <p className="text-slate-600 leading-relaxed">
//                   Chaque solution est conçue sur mesure, en tenant compte de vos spécificités et de vos objectifs commerciaux, pour garantir une protection optimale et une expérience utilisateur sans faille.
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Nos Valeurs Section - NOUVELLE SECTION AJOUTÉE */}
//       <section id="nos-valeurs" className={`py-20 bg-[#DDE6F1] transition-opacity duration-1000 ${activeSection === 'nos-valeurs' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
//               Nos Valeurs
//             </h2>
//             <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
//             <p className="text-slate-600 max-w-3xl mx-auto text-lg">
//               Un service à l'écoute de la clientèle basé sur la confiance et la confidentialité, la transparence, l'efficacité et le sens de la responsabilité.
//             </p>
//           </div>
          
//           <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
//             {ourValues.map((value, index) => (
//               <div 
//                 key={index} 
//                 className="bg-white p-6 rounded-xl shadow-md transition-all duration-300 hover:shadow-xl border border-gray-200 group hover:-translate-y-2"
//               >
//                 <div className="w-14 h-14 bg-[#F3DAB3] rounded-full mb-5 flex items-center justify-center group-hover:bg-[#2C3E50] transition-colors duration-300">
//                   <value.icon className="h-7 w-7 text-[#2C3E50] group-hover:text-[#F3DAB3]" />
//                 </div>
//                 <h3 className="text-xl font-bold text-[#2C3E50] mb-2">{value.title}</h3>
//                 <p className="text-slate-600 text-sm">{value.description}</p>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Nos Services Section - NOUVELLE SECTION AJOUTÉE */}
//       <section id="nos-services" className={`py-20 bg-white transition-opacity duration-1000 ${activeSection === 'nos-services' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
//               Nos Services
//             </h2>
//             <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
//             <p className="text-slate-600 max-w-3xl mx-auto text-lg">
//               Otenti est l'un des produits de Tsuriel Cyber et IT qui offre des services en CyberSécurité, Architecture, Data Security, Digitalisation, Audit, Design et Amélioration des Systèmes.
//             </p>
//           </div>
          
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//             {ourServices.map((service, index) => (
//               <div 
//                 key={index} 
//                 className="bg-white p-8 rounded-xl shadow-md transition-all duration-300 hover:shadow-xl border border-gray-200 group hover:-translate-y-2"
//               >
//                 <div className="w-16 h-16 bg-[#F3DAB3] bg-opacity-20 rounded-lg mb-6 flex items-center justify-center group-hover:bg-[#2C3E50] group-hover:bg-opacity-10 transition-colors duration-300">
//                   <service.icon className="h-8 w-8 text-[#2C3E50] group-hover:text-[#F3DAB3]" />
//                 </div>
//                 <h3 className="text-xl font-bold text-[#2C3E50] mb-3">{service.title}</h3>
//                 <p className="text-slate-600">{service.description}</p>
//                 <div className="mt-6">
//                   <button 
//                     className="inline-flex items-center text-[#F3DAB3] hover:text-[#DBC090] transition-colors duration-200"
//                     onClick={() => handleNavigateTo('/services')}
//                   >
//                     <span>En savoir plus</span>
//                     <ChevronRight className="ml-1 h-4 w-4" />
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>

//           <div className="mt-12 text-center">
//             <button 
//               className="px-8 py-4 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1A2533] transition-colors duration-300 shadow-lg inline-flex items-center"
//               onClick={() => handleNavigateTo('/services')}
//             >
//               <span>Découvrez tous nos services</span>
//               <ArrowRight className="ml-2 h-5 w-5" />
//             </button>
//           </div>
//         </div>
//       </section>

//       {/* Our Mission Section - Conservé de l'original */}
//       <section id="mission" className={`py-20 bg-gradient-to-b from-white to-[#DDE6F1] transition-opacity duration-1000 ${activeSection === 'mission' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
//               {t('our_mission')}
//             </h2>
//             <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
//             <p className="text-slate-600 max-w-2xl mx-auto text-lg">
//               {t('mission_description')}
//             </p>
//           </div>
          
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//             {coreValues.map((value, index) => (
//               <div 
//                 key={index} 
//                 className="bg-white p-8 rounded-xl shadow-md transition-all duration-300 hover:shadow-xl border border-gray-200 group hover:-translate-y-2"
//               >
//                 <div className="w-16 h-16 bg-[#F3DAB3] rounded-full mb-6 flex items-center justify-center group-hover:bg-[#2C3E50] transition-colors duration-300">
//                   <value.icon className="h-8 w-8 text-[#2C3E50] group-hover:text-[#F3DAB3]" />
//                 </div>
//                 <h3 className="text-xl font-bold text-[#2C3E50] mb-3">{value.title}</h3>
//                 <p className="text-slate-600">{value.description}</p>
//               </div>
//             ))}
//           </div>

//           <div className="mt-16 text-center">
//             <button 
//               className="px-6 py-3 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1A2533] transition-colors duration-300 shadow-md flex items-center mx-auto"
//               onClick={() => handleNavigateTo('/values')}
//             >
//               {t('learn_about_our_values')}
//               <ArrowRight className="ml-2 h-5 w-5" />
//             </button>
//           </div>
//         </div>
//       </section>

//       {/* Leadership Quote */}
//       <section id="quote" className={`py-24 bg-[#2C3E50] transition-opacity duration-1000 ${activeSection === 'quote' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="bg-white bg-opacity-5 rounded-xl p-12 shadow-2xl relative overflow-hidden backdrop-blur-sm">
//             <div className="absolute top-0 left-0 w-full h-full bg-[url('/pattern.svg')] opacity-5"></div>
//             <div className="relative z-10">
//               <div className="flex justify-center mb-8">
//                 <div className="w-20 h-20 rounded-full bg-[#F3DAB3] bg-opacity-20 flex items-center justify-center">
//                   <svg
//                     className="w-10 h-10 text-[#F3DAB3]"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
//                     />
//                   </svg>
//                 </div>
//               </div>

//               <p className="text-2xl md:text-3xl italic mb-8 font-light text-white text-center leading-relaxed">
//                 "{quote.text}"
//               </p>

//               <div className="flex items-center justify-center">
//                 <div className="w-16 h-16 rounded-full bg-[#F3DAB3] bg-opacity-20 mr-4 flex items-center justify-center">
//                   <span className="text-xl font-bold text-[#F3DAB3]">{quote.author.charAt(0)}</span>
//                 </div>
//                 <div>
//                   <p className="font-bold text-lg text-white">{quote.author}</p>
//                   <p className="text-sm text-slate-400">{quote.title}</p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Company History Timeline */}
//       <section id="history" className={`py-24 bg-[#DDE6F1] transition-opacity duration-1000 ${activeSection === 'history' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
//               {t('our_history')}
//             </h2>
//             <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
//             <p className="text-slate-600 max-w-2xl mx-auto text-lg">
//               {t('history_description')}
//             </p>
//           </div>
          
//           <div className="relative">
//             {/* Vertical line */}
//             <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#F3DAB3] rounded-full hidden md:block"></div>
            
//             <div className="space-y-16">
//               {/* 2020 */}
//               <div className="flex flex-col md:flex-row items-center">
//                 <div className="md:w-1/2 md:pr-12 md:text-right">
//                   <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
//                     <div className="text-[#2C3E50] font-bold text-xl mb-2">{t('history_2020_title')}</div>
//                     <p className="text-slate-600">{t('history_2020_description')}</p>
//                   </div>
//                 </div>
//                 <div className="md:w-1/2 flex justify-start md:justify-center py-4 md:px-0">
//                   <div className="bg-[#F3DAB3] w-10 h-10 rounded-full flex items-center justify-center border-4 border-white shadow-md">
//                     <span className="text-[#2C3E50] font-bold">20</span>
//                   </div>
//                 </div>
//               </div>
              
//               {/* 2022 */}
//               <div className="flex flex-col md:flex-row-reverse items-center">
//                 <div className="md:w-1/2 md:pl-12">
//                   <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
//                     <div className="text-[#2C3E50] font-bold text-xl mb-2">{t('history_2022_title')}</div>
//                     <p className="text-slate-600">{t('history_2022_description')}</p>
//                   </div>
//                 </div>
//                 <div className="md:w-1/2 flex justify-end md:justify-center py-4 md:px-0">
//                   <div className="bg-[#F3DAB3] w-10 h-10 rounded-full flex items-center justify-center border-4 border-white shadow-md">
//                     <span className="text-[#2C3E50] font-bold">22</span>
//                   </div>
//                 </div>
//               </div>
              
//               {/* 2024 */}
//               <div className="flex flex-col md:flex-row items-center">
//                 <div className="md:w-1/2 md:pr-12 md:text-right">
//                   <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
//                     <div className="text-[#2C3E50] font-bold text-xl mb-2">{t('history_2024_title')}</div>
//                     <p className="text-slate-600">{t('history_2024_description')}</p>
//                   </div>
//                 </div>
//                 <div className="md:w-1/2 flex justify-start md:justify-center py-4 md:px-0">
//                   <div className="bg-[#F3DAB3] w-10 h-10 rounded-full flex items-center justify-center border-4 border-white shadow-md">
//                     <span className="text-[#2C3E50] font-bold">24</span>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Team Section */}
//       <section id="team" className={`py-24 bg-white transition-opacity duration-1000 ${activeSection === 'team' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl font-bold text-[#2C3E50] mb-4">
//               {t('our_leadership_team')}
//             </h2>
//             <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
//             <p className="text-slate-600 max-w-2xl mx-auto text-lg">
//               {t('leadership_team_description')}
//             </p>
//           </div>
          
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//             {teamMembers.map((member, index) => (
//               <div 
//                 key={index} 
//                 className="group"
//               >
//                 <div 
//                   className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border border-gray-200"
//                   onClick={() => setSelectedMember(member)}
//                 >
//                   <div className="relative w-40 h-40 mx-auto mb-6">
//                     <div className="absolute inset-0 bg-[#F3DAB3] rounded-full opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
//                     <img
//                       src={member.image}
//                       alt={member.name}
//                       className="w-full h-full rounded-full object-cover relative z-10 border-4 border-white"
//                     />
//                   </div>
//                   <h3 className="text-xl font-bold text-[#2C3E50] mb-2">
//                     {member.name}
//                   </h3>
//                   <p className="text-slate-400 mb-4">
//                     {member.title}
//                   </p>
//                   <button className="inline-flex items-center text-[#F3DAB3] hover:text-[#DBC090] transition-colors duration-200">
//                     <span>{t('read_bio')}</span>
//                     <svg 
//                       className="w-4 h-4 ml-1 group-hover:ml-2 transition-all duration-200" 
//                       fill="none" 
//                       stroke="currentColor" 
//                       viewBox="0 0 24 24"
//                     >
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
//                     </svg>
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Connect Section */}
//       <section id="connect" className={`py-24 bg-gradient-to-r from-[#2C3E50] to-[#4A5568] transition-opacity duration-1000 ${activeSection === 'connect' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl font-bold text-white mb-4">
//               {t('connect_with_otenti')}
//             </h2>
//             <div className="w-24 h-1 bg-[#F3DAB3] mx-auto mb-6 rounded-full"></div>
//             <p className="text-slate-300 max-w-2xl mx-auto text-lg">
//               {t('connect_description')}
//             </p>
//           </div>
          
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//             {connectOptions.map((option, index) => (
//               <a
//                 key={index}
//                 href={option.link}
//                 className="group bg-[#2C3E50] hover:bg-[#F3DAB3] rounded-xl p-8 text-center transition-all duration-300 hover:-translate-y-2 border border-slate-600 hover:border-[#F3DAB3] shadow-lg"
//               >
//                 <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-600 group-hover:bg-[#2C3E50] flex items-center justify-center transition-colors duration-300">
//                   {option.icon}
//                 </div>
//                 <h3 className="text-xl font-bold text-white group-hover:text-[#2C3E50] mb-3 transition-colors duration-300">
//                   {option.title}
//                 </h3>
//                 <p className="text-slate-400 group-hover:text-[#2C3E50] transition-colors duration-300">
//                   {option.description}
//                 </p>
//               </a>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Expertise Section */}
//       <section id="expertise" className={`py-24 bg-[#DDE6F1] transition-opacity duration-1000 ${activeSection === 'expertise' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
//             <div className="space-y-6 order-2 md:order-1">
//               <h2 className="text-3xl font-bold text-[#2C3E50] mb-6">{t('expertise_title')}</h2>
//               <p className="text-[#2C3E50] leading-relaxed">
//                 {t('expertise_description')}
//               </p>
//               <ul className="space-y-4 mt-6">
//                 {[
//                   t('expertise_item_1'),
//                   t('expertise_item_2'),
//                   t('expertise_item_3')
//                 ].map((text, index) => (
//                   <li key={index} className="flex items-start">
//                     <div className="bg-[#F3DAB3] p-1 rounded-full mr-3 shadow-sm">
//                       <Check className="h-5 w-5 text-[#2C3E50]" />
//                     </div>
//                     <span className="text-[#2C3E50]">{text}</span>
//                   </li>
//                 ))}
//               </ul>
//               <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 pt-4">
//                 <button 
//                   onClick={() => handleNavigateTo('/services')}
//                   className="px-6 py-3 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1A2533] transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center"
//                 >
//                   {t('view_our_services')}
//                   <ArrowRight className="ml-2 h-5 w-5" />
//                 </button>
//                 <button 
//                   onClick={() => handleNavigateTo('/contact')}
//                   className="px-6 py-3 border-2 border-[#2C3E50] text-[#2C3E50] rounded-lg hover:bg-[#2C3E50] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center"
//                 >
//                   {t('contact_our_team')}
//                   <ArrowRight className="ml-2 h-5 w-5" />
//                 </button>
//               </div>
//             </div>
//             <div className="order-1 md:order-2">
//               <div className="bg-white p-8 rounded-xl shadow-lg transform transition-all duration-500 hover:scale-105">
//                 <div className="h-48 bg-gradient-to-r from-[#2C3E50] to-[#4A5568] rounded-lg mb-6 flex items-center justify-center">
//                   <Shield className="h-16 w-16 text-[#F3DAB3]" />
//                 </div>
//                 <h4 className="text-xl font-semibold text-[#2C3E50] mb-4">{t('security_excellence_title')}</h4>
//                 <p className="text-gray-600">
//                   {t('security_excellence_description')}
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Contact CTA Section - NOUVELLE SECTION AJOUTÉE */}
//       <section id="contact-cta" className={`py-20 bg-white transition-opacity duration-1000 ${activeSection === 'contact-cta' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="bg-gradient-to-r from-[#2C3E50] to-[#4A5568] rounded-2xl p-12 shadow-xl relative overflow-hidden">
//             <div className="absolute top-0 left-0 w-full h-full bg-[url('/pattern.svg')] opacity-10"></div>
//             <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
//               <div>
//                 <h2 className="text-3xl font-bold text-white mb-6">Prêt à sécuriser votre environnement numérique ?</h2>
//                 <p className="text-slate-300 mb-8 leading-relaxed">
//                   Contactez-nous dès aujourd'hui pour une consultation personnalisée et découvrez comment Otenti peut transformer votre approche de la cybersécurité.
//                 </p>
//                 <div className="flex flex-wrap gap-4">
//                   <button 
//                     className="px-6 py-3 bg-[#F3DAB3] text-[#2C3E50] rounded-lg hover:bg-[#DBC090] transition-colors duration-300 shadow-lg flex items-center"
//                     onClick={() => handleNavigateTo('/contact')}
//                   >
//                     <span>Nous contacter</span>
//                     <ArrowRight className="ml-2 h-5 w-5" />
//                   </button>
//                   <button 
//                     className="px-6 py-3 bg-transparent text-white border border-white rounded-lg hover:bg-white hover:text-[#2C3E50] transition-colors duration-300 flex items-center"
//                     onClick={() => handleNavigateTo('/demo')}
//                   >
//                     <span>Demander une démo</span>
//                     <ArrowRight className="ml-2 h-5 w-5" />
//                   </button>
//                 </div>
//               </div>
//               <div className="hidden md:block">
//                 <div className="relative">
//                   <div className="absolute inset-0 bg-[#F3DAB3] rounded-2xl -rotate-6 opacity-20"></div>
//                   <img
//                     src="/security.jpg"
//                     alt="Cybersécurité"
//                     className="relative w-full h-auto rounded-2xl shadow-2xl border border-slate-700"
//                   />
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Custom Modal */}
//       <CustomModal
//         isOpen={selectedMember !== null}
//         onClose={() => setSelectedMember(null)}
//       >
//         {selectedMember && (
//           <div>
//             <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
//               <div className="relative w-32 h-32">
//                 <div className="absolute inset-0 bg-[#F3DAB3] rounded-full opacity-20"></div>
//                 <img
//                   src={selectedMember.image}
//                   alt={selectedMember.name}
//                   className="w-full h-full rounded-full object-cover relative z-10 border-4 border-white"
//                 />
//               </div>
//               <div className="text-center md:text-left">
//                 <h3 className="text-2xl font-bold text-[#2C3E50] mb-2">{selectedMember.name}</h3>
//                 <p className="text-slate-400 mb-4">{selectedMember.title}</p>
//                 {selectedMember.linkedin && (
//                   <a 
//                     href={selectedMember.linkedin}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     className="inline-flex items-center text-[#F3DAB3] hover:text-[#DBC090] transition-colors duration-200"
//                   >
//                     <Linkedin size={16} className="mr-2" />
//                     <span>{t('connect_on_linkedin')}</span>
//                   </a>
//                 )}
//               </div>
//             </div>
//             <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
//               <h4 className="text-lg font-medium text-[#2C3E50] mb-3">{t('biography')}</h4>
//               <p className="text-slate-600 leading-relaxed">{selectedMember.bio}</p>
//             </div>
//           </div>
//         )}
//       </CustomModal>
      
//       <Footer />
//     </div>
//   );
// };
            


// import React, { useState, useEffect } from 'react';
// import Navbar from '../Components/Navbar';
// import { Footer } from '../Components/Footer';
// import { 
//   Monitor, 
//   MessageCircle, 
//   FileText,
//   X,
//   Linkedin
// } from 'lucide-react';
// import { useTranslation } from '../../TranslationContext';

// interface TeamMember {
//   name: string;
//   title: string;
//   image: string;
//   bio: string;
//   linkedin?: string;
// }

// interface Quote {
//   text: string;
//   author: string;
//   title: string;
// }

// interface CustomModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   children: React.ReactNode;
// }

// const CustomModal: React.FC<CustomModalProps> = ({ isOpen, onClose, children }) => {
//   const [isAnimating, setIsAnimating] = useState(false);
  
//   useEffect(() => {
//     if (isOpen) {
//       setIsAnimating(true);
//     }
//   }, [isOpen]);

//   const handleClose = () => {
//     setIsAnimating(false);
//     setTimeout(() => {
//       onClose();
//     }, 300);
//   };

//   if (!isOpen && !isAnimating) return null;

//   return (
//     <div className={`fixed inset-0 z-50 overflow-auto bg-slate-900 bg-opacity-40 backdrop-blur-sm flex justify-center items-center transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
//       <div className={`relative bg-white rounded-xl p-8 max-w-2xl m-4 shadow-xl transition-all duration-300 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'} border border-slate-200`}>
//         <button 
//           onClick={handleClose}
//           className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors duration-200"
//         >
//           <X size={24} />
//         </button>
//         {children}
//       </div>
//     </div>
//   );
// };

// export const About: React.FC = () => {
//   const { t } = useTranslation();
//   const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
//   const [activeSection, setActiveSection] = useState<string>('');

//   useEffect(() => {
//     const handleScroll = () => {
//       const sections = document.querySelectorAll('section[id]');
      
//       sections.forEach(section => {
//         const sectionTop = section.getBoundingClientRect().top;
        
//         if (sectionTop < window.innerHeight / 2) {
//           setActiveSection(section.id);
//         }
//       });
//     };

//     window.addEventListener('scroll', handleScroll);
//     return () => window.removeEventListener('scroll', handleScroll);
//   }, []);

//   const teamMembers: TeamMember[] = [
//     {
//       name: "xxx xxx",
//       title: t('ceo'),
//       image: "/avatar.png",
//       bio: t('ceo_bio'),
//       linkedin: "https://linkedin.com"
//     },
//     {
//       name: "YYY YYY",
//       title: t('cto'),
//       image: "/avatar.png",
//       bio: t('cto_bio'),
//       linkedin: "https://linkedin.com"
//     },
//     {
//       name: "ZZZ ZZZ",
//       title: t('cmo'),
//       image: "/avatar.png",
//       bio: t('cmo_bio'),
//       linkedin: "https://linkedin.com"
//     }
//   ];

//   const quote: Quote = {
//     text: t('leadership_quote'),
//     author: "XXX XXX",
//     title: t('ceo')
//   };

//   const connectOptions = [
//     {
//       icon: <Monitor className="w-12 h-12 text-slate-400 group-hover:text-white transition-colors duration-300" />,
//       title: t('free_demo'),
//       description: t('free_demo_description'),
//       link: "#"
//     },
//     {
//       icon: <MessageCircle className="w-12 h-12 text-slate-400 group-hover:text-white transition-colors duration-300" />,
//       title: t('contact_us'),
//       description: t('contact_us_description'),
//       link: "#"
//     },
//     {
//       icon: <FileText className="w-12 h-12 text-slate-400 group-hover:text-white transition-colors duration-300" />,
//       title: t('blog'),
//       description: t('blog_description'),
//       link: "#"
//     }
//   ];

//   return (
//     <div className="min-h-screen bg-white">
//       <Navbar />

//       {/* Hero Section */}
//       <div className="relative">
//         <div className="absolute inset-0 bg-gradient-to-r from-slate-900 to-slate-800 opacity-90"></div>
//         <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
//           <div className="flex flex-col md:flex-row gap-12 items-center">
//             {/* Text content */}
//             <div className="prose max-w-none flex-1 text-center md:text-left">
//               <h1 className="text-5xl font-bold text-white mb-6">
//                 {t('about_otenti')}<span className="text-sky-400">.</span>
//               </h1>
//               <p className="text-slate-300 text-xl font-light leading-relaxed mb-8">
//                 {t('otenti_description')}
//               </p>
//               <div className="flex flex-wrap gap-4 justify-center md:justify-start">
//                 <button className="px-6 py-3 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors duration-300 shadow-lg shadow-sky-500/20">
//                   {t('our_services')}
//                 </button>
//                 <button className="px-6 py-3 bg-transparent text-white border border-slate-400 rounded-lg hover:bg-slate-700 transition-colors duration-300">
//                   {t('contact_us')}
//                 </button>
//               </div>
//             </div>

//             {/* Image */}
//             <div className="flex-1 relative">
//               <div className="absolute inset-0 bg-sky-400 rounded-2xl -rotate-6 opacity-20"></div>
//               <img
//                 src="/home.jpeg"
//                 alt={t('about_otenti')}
//                 className="relative w-full h-auto rounded-2xl shadow-2xl border border-slate-700"
//               />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Leadership Quote */}
//       <section id="quote" className={`py-24 transition-opacity duration-1000 ${activeSection === 'quote' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-12 shadow-2xl relative overflow-hidden">
//             <div className="absolute top-0 left-0 w-full h-full bg-[url('/pattern.svg')] opacity-5"></div>
//             <div className="relative z-10">
//               <div className="flex justify-center mb-8">
//                 <div className="w-20 h-20 rounded-full bg-sky-400 bg-opacity-20 flex items-center justify-center">
//                   <svg
//                     className="w-10 h-10 text-sky-400"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
//                     />
//                   </svg>
//                 </div>
//               </div>

//               <p className="text-2xl md:text-3xl italic mb-8 font-light text-white text-center leading-relaxed">
//                 "{quote.text}"
//               </p>

//               <div className="flex items-center justify-center">
//                 <div className="w-16 h-16 rounded-full bg-sky-400 bg-opacity-20 mr-4 flex items-center justify-center">
//                   <span className="text-xl font-bold text-sky-400">{quote.author.charAt(0)}</span>
//                 </div>
//                 <div>
//                   <p className="font-bold text-lg text-white">{quote.author}</p>
//                   <p className="text-sm text-slate-400">{quote.title}</p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Team Section */}
//       <section id="team" className={`py-24 bg-slate-50 transition-opacity duration-1000 ${activeSection === 'team' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl font-bold text-slate-800 mb-4">
//               {t('our_leadership_team')}
//             </h2>
//             <div className="w-24 h-1 bg-sky-400 mx-auto mb-6"></div>
//             <p className="text-slate-600 max-w-2xl mx-auto text-lg">
//               {t('leadership_team_description')}
//             </p>
//           </div>
          
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//             {teamMembers.map((member, index) => (
//               <div 
//                 key={index} 
//                 className="group"
//               >
//                 <div 
//                   className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border border-slate-200"
//                   onClick={() => setSelectedMember(member)}
//                 >
//                   <div className="relative w-40 h-40 mx-auto mb-6">
//                     <div className="absolute inset-0 bg-sky-400 rounded-full opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
//                     <img
//                       src={member.image}
//                       alt={member.name}
//                       className="w-full h-full rounded-full object-cover relative z-10 border-4 border-white"
//                     />
//                   </div>
//                   <h3 className="text-xl font-bold text-slate-800 mb-2">
//                     {member.name}
//                   </h3>
//                   <p className="text-slate-400 mb-4">
//                     {member.title}
//                   </p>
//                   <button className="inline-flex items-center text-sky-500 hover:text-sky-600 transition-colors duration-200">
//                     <span>{t('read_bio')}</span>
//                     <svg 
//                       className="w-4 h-4 ml-1 group-hover:ml-2 transition-all duration-200" 
//                       fill="none" 
//                       stroke="currentColor" 
//                       viewBox="0 0 24 24"
//                     >
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
//                     </svg>
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Connect Section */}
//       <section id="connect" className={`py-24 bg-gradient-to-r from-slate-800 to-slate-700 transition-opacity duration-1000 ${activeSection === 'connect' ? 'opacity-100' : 'opacity-80'}`}>
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="text-center mb-16">
//             <h2 className="text-4xl font-bold text-white mb-4">
//               {t('connect_with_otenti')}
//             </h2>
//             <div className="w-24 h-1 bg-sky-400 mx-auto mb-6"></div>
//             <p className="text-slate-300 max-w-2xl mx-auto text-lg">
//               {t('connect_description')}
//             </p>
//           </div>
          
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//             {connectOptions.map((option, index) => (
//               <a
//                 key={index}
//                 href={option.link}
//                 className="group bg-slate-700 hover:bg-sky-500 rounded-xl p-8 text-center transition-all duration-300 hover:-translate-y-2 border border-slate-600 hover:border-sky-400 shadow-lg"
//               >
//                 <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-600 group-hover:bg-sky-600 flex items-center justify-center transition-colors duration-300">
//                   {option.icon}
//                 </div>
//                 <h3 className="text-xl font-bold text-white mb-3">
//                   {option.title}
//                 </h3>
//                 <p className="text-slate-400 group-hover:text-white transition-colors duration-300">
//                   {option.description}
//                 </p>
//               </a>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Custom Modal */}
//       <CustomModal
//         isOpen={selectedMember !== null}
//         onClose={() => setSelectedMember(null)}
//       >
//         {selectedMember && (
//           <div>
//             <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
//               <div className="relative w-32 h-32">
//                 <div className="absolute inset-0 bg-sky-400 rounded-full opacity-20"></div>
//                 <img
//                   src={selectedMember.image}
//                   alt={selectedMember.name}
//                   className="w-full h-full rounded-full object-cover relative z-10 border-4 border-white"
//                 />
//               </div>
//               <div className="text-center md:text-left">
//                 <h3 className="text-2xl font-bold text-slate-800 mb-2">{selectedMember.name}</h3>
//                 <p className="text-slate-400 mb-4">{selectedMember.title}</p>
//                 {selectedMember.linkedin && (
//                   <a 
//                     href={selectedMember.linkedin}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     className="inline-flex items-center text-sky-500 hover:text-sky-600 transition-colors duration-200"
//                   >
//                     <Linkedin size={16} className="mr-2" />
//                     <span>{t('connect_on_linkedin')}</span>
//                   </a>
//                 )}
//               </div>
//             </div>
//             <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
//               <h4 className="text-lg font-medium text-slate-800 mb-3">{t('biography')}</h4>
//               <p className="text-slate-600 leading-relaxed">{selectedMember.bio}</p>
//             </div>
//           </div>
//         )}
//       </CustomModal>
      
//       <Footer />
//     </div>
//   );
// };

// export default About;
